import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  SmartphoneIcon,
  PhoneIcon,
  EmailIcon,
  LocationIcon,
  DocumentIcon,
  WhatsAppIcon,
  WrenchIcon
} from '../components/Icons';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 2000);
  };

  // Contact info
  const WHATSAPP_DISPLAY = '060 938 2465';
  const WHATSAPP_LINK = 'https://wa.me/27609382465';
  const LANDLINE_DISPLAY = '010 500 0105';
  const LANDLINE_LINK = 'tel:+27105000105';
  const ADDRESS_LINES = ['138 Walton Ave, Carlswald', 'Midrand, 1684', 'South Africa'];
  const MAPS_QUERY_URL = 'https://www.google.com/maps/search/?api=1&query=138+Walton+Ave,+Carlswald,+Midrand,+1684';
  const MAPS_EMBED_SRC = 'https://www.google.com/maps?q=138+Walton+Ave,+Carlswald,+Midrand,+1684&output=embed';

  // Style for visible buttons (no hover needed)
  const visibleButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0077b6',
    color: '#fff',
    border: '2px solid #0077b6',
    borderRadius: '8px',
    padding: '10px 16px',
    textDecoration: 'none',
    fontWeight: 600,
    transition: 'none',
    cursor: 'pointer'
  };

  return (
    <div className="contact-page">
      <Helmet>
        <title>Contact Pure Drop Technicals - Water Business Experts</title>
        <meta
          name="description"
          content="Contact Pure Drop Technicals in Midrand for water business solutions. Call 010 500 0105 or WhatsApp 060 938 2465. Address: 138 Walton Ave, Carlswald, Midrand, 1684."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="contact-hero section small-hero">
        <div className="container">
          <div className="contact-hero-content">
            <div className="contact-text">
              <h1>Contact Our Water Business Experts</h1>
              <p className="lead">
                Call, WhatsApp, or visit our Midrand office. We reply within hours during business time.
              </p>
              <div className="hero-cta">
                <a href={LANDLINE_LINK} className="btn btn-light">Call {LANDLINE_DISPLAY}</a>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  WhatsApp {WHATSAPP_DISPLAY}
                </a>
                <a href="#contact-methods" className="btn btn-outline">All Contact Options</a>
              </div>
            </div>
            <div className="contact-visual" aria-hidden="true">
              <div className="contact-icon-badge">
                <WrenchIcon size={56} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section id="contact-methods" className="contact-methods section">
        <div className="container">
          <div className="contact-grid">

            <div className="contact-card featured">
              <div className="contact-icon">
                <SmartphoneIcon size={48} className="contact-icon-svg" />
              </div>
              <h3>WhatsApp Chat</h3>
              <p>Fastest replies during business hours</p>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="contact-link">
                {WHATSAPP_DISPLAY}
              </a>
              <span className="availability">
                Thu–Fri 8:30–17:00 • Sat 8:30–13:00 • Sun Closed • Mon–Wed 8:30–17:00
              </span>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <PhoneIcon size={48} className="contact-icon-svg" />
              </div>
              <h3>Phone Support</h3>
              <p>Speak directly with our team</p>
              <a href={LANDLINE_LINK} className="contact-link">
                {LANDLINE_DISPLAY}
              </a>
              <span className="availability">Mon–Fri 8:30–17:00 • Sat 8:30–13:00</span>
            </div>

            <div className="contact-card">
              <div className="contact-icon">
                <EmailIcon size={48} className="contact-icon-svg" />
              </div>
              <h3>Email Support</h3>
              <p>Send details and attachments</p>
              <a href="mailto:info@watershopinstallations.co.za" className="contact-link">
                info@watershopinstallations.co.za
              </a>
              <span className="availability">Replies within 2–4 hours (business hours)</span>
            </div>

            <div className="contact-card emergency">
              <div className="contact-icon" aria-hidden="true">🆘</div>
              <h3>After-Hours Support</h3>
              <p>For urgent issues after hours, message us on WhatsApp</p>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="contact-link">
                {WHATSAPP_DISPLAY}
              </a>
              <span className="availability">Response subject to availability</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="contact-form-section section">
        <div className="container">
          <div className="contact-content">

            {/* Contact Form */}
            <div className="form-container">
              <div className="form-header">
                <h2>Send Us a Message</h2>
                <p>Fill out the form and we’ll reply shortly</p>
              </div>

              {submitStatus === 'success' ? (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <h3>Message Sent Successfully!</h3>
                  <p>Thanks for contacting Pure Drop Technicals. We’ll respond within 2–4 hours during business hours.</p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+27 10 500 0105"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="subject">Subject</label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Subject</option>
                        <option value="general-inquiry">General Inquiry</option>
                        <option value="quote-request">Quote Request</option>
                        <option value="technical-support">Technical Support</option>
                        <option value="business-consultation">Business Consultation</option>
                        <option value="partnership">Partnership Opportunity</option>
                        <option value="complaint">Complaint/Issue</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="5"
                      placeholder="Please include your location and specific requirements..."
                      required
                    ></textarea>
                  </div>

                  <button type="submit" className="btn btn-primary btn-large" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending Message...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Company Information */}
            <div className="company-info">
              <div className="info-section">
                <h3>Our Office</h3>
                <div className="info-item">
                  <div className="info-icon">
                    <LocationIcon size={24} className="info-icon-svg" />
                  </div>
                  <div className="info-details">
                    <h4>Head Office</h4>
                    <p>
                      {ADDRESS_LINES[0]}<br />
                      {ADDRESS_LINES[1]}<br />
                      {ADDRESS_LINES[2]}
                    </p>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
                      <a href={MAPS_QUERY_URL} target="_blank" rel="noopener noreferrer" style={visibleButtonStyle}>
                        Open in Google Maps
                      </a>
                      <a href={LANDLINE_LINK} style={visibleButtonStyle}>
                        Call {LANDLINE_DISPLAY}
                      </a>
                      <a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...visibleButtonStyle, background: '#25D366', borderColor: '#25D366' }}
                      >
                        WhatsApp {WHATSAPP_DISPLAY}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Embedded Google Map */}
                <div className="map-wrapper" style={{ marginTop: 16 }}>
                  <div
                    className="map-embed"
                    style={{
                      position: 'relative',
                      width: '100%',
                      paddingBottom: '56.25%',
                      borderRadius: 12,
                      overflow: 'hidden',
                      boxShadow: '0 6px 24px rgba(0,0,0,0.12)'
                    }}
                  >
                    <iframe
                      title="Pure Drop Technicals - Map"
                      src={MAPS_EMBED_SRC}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="info-section">
                <h3>Business Hours</h3>
                <div className="hours-list">
                  <div className="hours-item"><span className="day">Monday</span><span className="time">8:30 AM – 5:00 PM</span></div>
                  <div className="hours-item"><span className="day">Tuesday</span><span className="time">8:30 AM – 5:00 PM</span></div>
                  <div className="hours-item"><span className="day">Wednesday</span><span className="time">8:30 AM – 5:00 PM</span></div>
                  <div className="hours-item"><span className="day">Thursday</span><span className="time">8:30 AM – 5:00 PM</span></div>
                  <div className="hours-item"><span className="day">Friday</span><span className="time">8:30 AM – 5:00 PM</span></div>
                  <div className="hours-item"><span className="day">Saturday</span><span className="time">8:30 AM – 1:00 PM</span></div>
                  <div className="hours-item"><span className="day">Sunday</span><span className="time">Closed</span></div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="info-section">
                <h3>Quick Actions</h3>
                <div className="quick-actions">
                  <a href="/request" className="action-btn">
                    <DocumentIcon size={16} className="action-icon" /> Request Quote
                  </a>
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="action-btn">
                    <WhatsAppIcon size={16} className="action-icon" /> WhatsApp Chat
                  </a>
                  <a href="/services" className="action-btn">
                    <WrenchIcon size={16} className="action-icon" /> View Services
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
